import React from 'react';
import { Analytics } from "@vercel/analytics/react"
import { Bug, Search, CheckCircle, Shield, AlertTriangle, Target } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageSEO from '../../components/PageSEO';

const SITE_URL = 'https://luai.io';

const VulnerabilityAssessments = () => {
  const handleGetEvaluation = () => {
    window.location.href = '/#contact';
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 500);
  };
  return (
    <>
      <PageSEO
        title="Red Team Operations – Advanced Adversary Simulation | Luai"
        description="Professional Red Team operations and advanced persistent threat simulation. Real-world attack scenarios to test your defenses."
        image={`${SITE_URL}/assets/og/red-team-operations.jpg`}
        type="article"
        url={`${SITE_URL}/services/red-team-operations`}
        schema={{
          "@context":"https://schema.org",
          "@type":"Service",
          name:"Red Team Operations",
          serviceType:"Red Teaming / Adversary Simulation",
          provider:{ "@type":"Organization", name:"Luai", url:SITE_URL },
          areaServed:"Worldwide",
          audience:{ "@type":"Audience", audienceType:"SMB, Enterprise, Startup" }
        }}
      />

      <Header />

      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-red-900 relative overflow-hidden pt-16">
        <video 
          className="absolute inset-0 w-full h-full object-cover z-0 opacity-30"
          autoPlay 
          muted 
          loop 
          playsInline
        >
          <source src="/assets/0_Abstract_Design_Data_Visualization_3840x2160.mp4" type="video/mp4" />
        </video>
        
        <div className="absolute inset-0 bg-black bg-opacity-60 z-10"></div>
        
        <div className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-purple-500/20 border border-purple-500/30 rounded-full px-6 py-3 mb-6">
              <Bug className="w-6 h-6 text-purple-400 mr-2" />
              <span className="text-purple-300 font-semibold">Find Vulnerabilities First</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent mb-6">
              Vulnerability Assessments
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              <strong className="text-purple-400">Find your weaknesses before hackers do.</strong> 
              Our comprehensive vulnerability assessments identify security gaps that could cost you everything.
            </p>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
            
            {/* The Reality Check */}
            <section className="mb-12">
              <div className="bg-gradient-to-r from-red-500/15 to-orange-500/15 rounded-2xl p-8 mb-8 border border-red-500/30">
                <h2 className="text-2xl font-bold text-white mb-4">🔍 The Hidden Threat Landscape</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  <strong className="text-red-400">The average organization has 57 critical vulnerabilities at any given time.</strong> 
                  Every single one is a potential gateway for attackers. While you're reading this, automated 
                  scanners are probing your systems, looking for weaknesses to exploit.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Here's what keeps me up at night: <strong className="text-purple-400">most breaches happen through vulnerabilities 
                  that have been known for months or even years.</strong> The Equifax breach? A known vulnerability that wasn't patched. 
                  WannaCry? Exploited a vulnerability Microsoft had already fixed. Your competitors aren't getting breached 
                  because they're unlucky - they're getting breached because they don't know what's broken.
                </p>
              </div>
            </section>

            {/* What We Actually Do */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8 flex items-center">
                <Search className="w-8 h-8 text-purple-400 mr-3" />
                Military-Grade Vulnerability Hunting
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-purple-400 mb-4">Beyond Automated Scanning</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Anyone can run a vulnerability scanner. What makes the difference is knowing how to interpret 
                    the results, understanding which vulnerabilities actually matter, and finding the hidden 
                    weaknesses that automated tools miss completely.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Manual validation of all critical findings</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Business impact assessment for each vulnerability</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Attack path analysis and exploitation scenarios</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Zero-day and logic flaw identification</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold text-red-400 mb-4">Comprehensive Attack Surface Mapping</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    I don't just scan your known systems. I map your entire attack surface the same way 
                    attackers do - finding forgotten servers, shadow IT, exposed databases, and 
                    misconfigured cloud resources that you didn't even know existed.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">External attack surface reconnaissance</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Internal network vulnerability assessment</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Cloud infrastructure security evaluation</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Web application and API vulnerability testing</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Our Proven Assessment Methodology */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">Our Proven Assessment Methodology</h2>
              
              <div className="grid gap-6">
                <div className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/50">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                    <h3 className="text-xl font-semibold text-purple-400">Intelligence Gathering & Reconnaissance</h3>
                  </div>
                  <p className="text-gray-300 mb-3">
                    Like a military operation, we start with intelligence. I map your entire digital footprint, 
                    identify all systems and services, and understand your technology stack before any scanning begins.
                  </p>
                  <div className="text-sm text-gray-400">
                    ✓ OSINT reconnaissance ✓ Asset discovery ✓ Technology fingerprinting ✓ Attack surface mapping
                  </div>
                </div>

                <div className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/50">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                    <h3 className="text-xl font-semibold text-red-400">Automated & Manual Vulnerability Discovery</h3>
                  </div>
                  <p className="text-gray-300 mb-3">
                    I use enterprise-grade scanning tools combined with manual testing techniques to identify 
                    vulnerabilities across your entire infrastructure, applications, and configurations.
                  </p>
                  <div className="text-sm text-gray-400">
                    ✓ Network vulnerability scanning ✓ Web application testing ✓ Configuration review ✓ Manual verification
                  </div>
                </div>

                <div className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/50">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                    <h3 className="text-xl font-semibold text-orange-400">Risk Analysis & Prioritization</h3>
                  </div>
                  <p className="text-gray-300 mb-3">
                    Not all vulnerabilities are created equal. I analyze each finding for exploitability, 
                    business impact, and likelihood of attack to help you focus on what matters most.
                  </p>
                  <div className="text-sm text-gray-400">
                    ✓ CVSS scoring ✓ Business impact assessment ✓ Exploit availability analysis ✓ Attack scenario modeling
                  </div>
                </div>

                <div className="bg-slate-700/30 rounded-xl p-6 border border-slate-600/50">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                    <h3 className="text-xl font-semibold text-green-400">Detailed Reporting & Remediation Guidance</h3>
                  </div>
                  <p className="text-gray-300 mb-3">
                    You get executive summaries for leadership and detailed technical guidance for your IT team. 
                    Every vulnerability includes specific remediation steps, not just generic advice.
                  </p>
                  <div className="text-sm text-gray-400">
                    ✓ Executive dashboard ✓ Technical remediation steps ✓ Proof-of-concept examples ✓ Retest validation
                  </div>
                </div>
              </div>
            </section>

            {/* What We Actually Find */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">What We Actually Find</h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-xl p-6 border border-red-500/30">
                  <h3 className="text-xl font-semibold text-white mb-4">💀 Critical Infrastructure Flaws</h3>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Unpatched systems with known exploits</li>
                    <li>• Default credentials on critical systems</li>
                    <li>• Exposed databases and file shares</li>
                    <li>• Misconfigured firewalls and ACLs</li>
                    <li>• Weak encryption implementations</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 rounded-xl p-6 border border-orange-500/30">
                  <h3 className="text-xl font-semibold text-white mb-4">🌐 Web Application Vulnerabilities</h3>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• SQL injection and code execution flaws</li>
                    <li>• Authentication and session bypass</li>
                    <li>• Cross-site scripting (XSS) vulnerabilities</li>
                    <li>• Insecure API endpoints</li>
                    <li>• Business logic vulnerabilities</li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-xl p-6 border border-red-500/30">
                  <h3 className="text-xl font-semibold text-white mb-4">☁️ Cloud Security Gaps</h3>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• Publicly accessible cloud storage</li>
                    <li>• Overprivileged IAM configurations</li>
                    <li>• Unencrypted data at rest and transit</li>
                    <li>• Missing security monitoring</li>
                    <li>• Container and Kubernetes misconfigurations</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* The Numbers Don't Lie */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-8">The Numbers Don't Lie</h2>
              
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-xl p-6 border border-red-500/30 text-center">
                  <div className="text-3xl font-bold text-red-400 mb-3">18.4</div>
                  <h3 className="text-lg font-semibold text-white mb-2">Billion Records Exposed</h3>
                  <p className="text-gray-300 text-sm">Through unpatched vulnerabilities in 2023 alone</p>
                </div>

                <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-500/30 text-center">
                  <div className="text-3xl font-bold text-orange-400 mb-3">287 Days</div>
                  <h3 className="text-lg font-semibold text-white mb-2">Average Time to Identify</h3>
                  <p className="text-gray-300 text-sm">How long it takes organizations to find a breach</p>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30 text-center">
                  <div className="text-3xl font-bold text-yellow-400 mb-3">$4.45M</div>
                  <h3 className="text-lg font-semibold text-white mb-2">Average Breach Cost</h3>
                  <p className="text-gray-300 text-sm">Global average cost of a data breach in 2023</p>
                </div>

                <div className="bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-xl p-6 border border-green-500/30 text-center">
                  <div className="text-3xl font-bold text-green-400 mb-3">73%</div>
                  <h3 className="text-lg font-semibold text-white mb-2">Cost Reduction</h3>
                  <p className="text-gray-300 text-sm">When vulnerabilities are found and fixed proactively</p>
                </div>
              </div>
            </section>

            {/* Final CTA */}
            <section className="text-center">
              <div className="bg-gradient-to-r from-purple-500/15 to-pink-500/15 rounded-2xl p-8 border border-purple-500/30">
                <h2 className="text-3xl font-bold text-white mb-4">Every Day You Wait, Your Risk Grows</h2>
                <p className="text-xl text-gray-300 mb-6">
                  <strong className="text-red-400">New vulnerabilities are discovered every day.</strong> 
                  The longer you wait, the more exposed you become. While you're deciding whether to act, 
                  attackers are already scanning for the vulnerabilities I could find for you.
                </p>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-red-500/20 p-6 rounded-xl border border-red-500/40">
                    <div className="text-3xl font-bold text-red-400 mb-2">25,000+</div>
                    <p className="text-gray-300">New vulnerabilities discovered annually</p>
                  </div>
                  <div className="bg-orange-500/20 p-6 rounded-xl border border-orange-500/40">
                    <div className="text-3xl font-bold text-orange-400 mb-2">22 Minutes</div>
                    <p className="text-gray-300">Average time for attackers to find and exploit a critical vulnerability</p>
                  </div>
                  <div className="bg-purple-500/20 p-6 rounded-xl border border-purple-500/40">
                    <div className="text-3xl font-bold text-purple-400 mb-2">95%</div>
                    <p className="text-gray-300">Of successful attacks exploit known vulnerabilities</p>
                  </div>
                </div>

                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-6 mb-8">
                  <h3 className="text-xl font-bold text-purple-400 mb-3">🛡️ The Cost of Not Knowing</h3>
                  <p className="text-gray-300">
                    Think vulnerability assessments are expensive? Try dealing with a data breach. <strong className="text-white">
                    The average cost of a breach is $4.45 million, but the average cost of a comprehensive 
                    vulnerability assessment is less than 1% of that.</strong> You're not buying a security 
                    report - you're buying peace of mind and business continuity insurance.
                  </p>
                </div>

                <button
                  onClick={handleGetEvaluation}
                  className="inline-flex items-center justify-center px-10 py-4 text-lg font-bold text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                >
                  Start Your Vulnerability Assessment
                </button>
                <p className="text-gray-400 text-sm mt-4">Comprehensive scanning • Expert analysis • Actionable results • Risk prioritization</p>
              </div>
            </section>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default VulnerabilityAssessments;